import React, { useEffect, useState } from 'react';
import { collection, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Link } from 'react-router-dom';
import { db, functions } from '../../../firebase-client';
import { safeOnSnapshot } from '../../../utils/firestoreSubscriptions';
import EditButton from '../../ui/EditButton';

const listPublicCourses = httpsCallable(functions, 'listPublicCourses');

function when(value) {
  const date = new Date(value); return Number.isNaN(date.getTime()) ? 'Date not set' : date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

function money(minor, currency = 'GBP') {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(minor || 0) / 100);
  } catch (error) {
    return `${(Number(minor || 0) / 100).toFixed(2)} ${currency}`;
  }
}

export default function CoursesCard({ teamId, isAdmin = false, publicEventsSlug = '', onEdit }) {
  const [courses, setCourses] = useState([]);
  const [bookableCourses, setBookableCourses] = useState([]);
  const [loadingBookable, setLoadingBookable] = useState(false);
  const [bookableError, setBookableError] = useState('');
  useEffect(() => {
    if (!teamId || !isAdmin) return undefined;
    return safeOnSnapshot(query(collection(db, 'teams', teamId, 'courses'), orderBy('name')), (snapshot) => {
      setCourses(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    }, undefined, { label: 'courses-admin', teamId });
  }, [teamId, isAdmin]);
  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!publicEventsSlug) {
        if (active) {
          setBookableCourses([]);
          setLoadingBookable(false);
        }
        return;
      }
      setLoadingBookable(true);
      setBookableError('');
      try {
        const result = await listPublicCourses({ clubSlug: publicEventsSlug });
        if (!active) return;
        setBookableCourses((result.data?.courses || [])
          .filter((course) => course.booked || (course.ticketTypes || []).length > 0)
          .sort((left, right) => String(left.sessions?.[0]?.start || '').localeCompare(String(right.sessions?.[0]?.start || ''))));
      } catch (error) {
        if (!active) return;
        setBookableCourses([]);
        setBookableError('Courses could not be loaded just now.');
      } finally {
        if (active) setLoadingBookable(false);
      }
    };
    load();
    window.addEventListener('focus', load);
    return () => {
      active = false;
      window.removeEventListener('focus', load);
    };
  }, [publicEventsSlug]);
  const published = courses.filter((course) => course.status === 'published').length;
  const drafts = courses.filter((course) => course.status === 'draft' || course.hasUnpublishedChanges).length;
  const nextCourse = courses
    .filter((course) => course.status === 'published')
    .sort((left, right) => String(left.sessions?.[0]?.start || '').localeCompare(String(right.sessions?.[0]?.start || '')))[0];
  const bookablePreviewLimit = isAdmin ? 1 : 3;
  const visibleBookable = bookableCourses.slice(0, bookablePreviewLimit);
  const hiddenBookableCount = Math.max(0, bookableCourses.length - visibleBookable.length);
  return <div className="team-card">
    <div className="card-header-with-icon"><span className="card-title">Courses</span>{isAdmin && <EditButton section="courses" onEdit={onEdit} />}</div>
    <div className="card-body">
      {isAdmin && <>
        {courses.length === 0 ? <p className="text-muted">Create a multi-session course with tickets, an attached booking form, attendance and conversion.</p> : <div className="courses-card__summary"><div><strong>{courses.length}</strong><span>Total courses</span></div><div><strong>{published}</strong><span>Published</span></div><div><strong>{drafts}</strong><span>Draft changes</span></div></div>}
        {nextCourse?.sessions?.[0]?.start && <p className="small"><strong>Next:</strong> {nextCourse.name} · {when(nextCourse.sessions[0].start)}</p>}
        <div className="courses-card__actions"><button type="button" className="button button-primary" onClick={() => onEdit?.({})}>Manage courses</button><button type="button" className="button button-secondary" onClick={() => onEdit?.({ createNew: true })}>Create course</button></div>
      </>}
      <section className={`courses-card__booking${isAdmin ? ' courses-card__booking--admin' : ''}`} aria-labelledby="bookable-courses-heading">
        <div className="courses-card__booking-heading">
          <div>
            <strong id="bookable-courses-heading">{isAdmin ? 'Member booking view' : 'Available courses'}</strong>
            <p className="text-muted">{isAdmin ? 'These are the courses currently available to this account.' : 'Choose a course to view all sessions, select attendees and book.'}</p>
          </div>
          {publicEventsSlug && <Link className="button-link" to={`/events/${publicEventsSlug}?view=courses`}>View all{hiddenBookableCount > 0 ? ` (${bookableCourses.length})` : ''}</Link>}
        </div>
        {loadingBookable ? <p className="text-muted">Loading available courses…</p> : bookableError ? <p className="error-message">{bookableError}</p> : visibleBookable.length === 0 ? <p className="text-muted">No courses are currently available for this account.</p> : <div className="courses-card__list">
          {visibleBookable.map((course) => {
            const prices = (course.ticketTypes || []).map((ticket) => Number(ticket.priceMinor || 0));
            const lowest = prices.length ? Math.min(...prices) : null;
            const firstSession = (course.sessions || []).find((session) => session.start);
            const bookingPath = `/events/${publicEventsSlug}?view=courses&course=${encodeURIComponent(course.slug)}`;
            return <article className="courses-card__course" key={course.slug}>
              <div className="courses-card__course-copy">
                <strong>{course.name}</strong>
                <span>{firstSession ? when(firstSession.start) : 'Dates to follow'} · {(course.sessions || []).length} {(course.sessions || []).length === 1 ? 'session' : 'sessions'}</span>
                <span>{course.booked ? `${(course.myBookings || []).flatMap((booking) => booking.attendees || []).length || 1} confirmed course ticket${(course.myBookings || []).flatMap((booking) => booking.attendees || []).length === 1 ? '' : 's'}` : `${course.capacityRemaining} places remaining`}{!course.booked && lowest != null ? ` · From ${money(lowest, course.currency)}` : ''}</span>
                {course.booked && (course.myBookings || []).length > 0 && <span className="courses-card__attendees">
                  {(course.myBookings || []).flatMap((booking) => booking.attendees || []).map((attendee) => `${attendee.name} — ${attendee.ticketName}`).join(' · ')}
                </span>}
              </div>
              <Link className={`button ${course.booked ? 'button-secondary' : 'button-primary'}`} to={course.booked && course.ticketWalletPath ? course.ticketWalletPath : bookingPath}>{course.booked ? 'View QR tickets' : 'Book course'}</Link>
            </article>;
          })}
        </div>}
      </section>
    </div>
  </div>;
}
